/**
 * Utility functions for HPC Jobs data transformation and formatting.
 */

import { HpcJob, HpcWorkflow, JobStatus, WorkflowStatus } from "@/types/hpc-jobs"
import type { WorkflowListingEntry } from "@/services/hpcJobsService"

/**
 * Safely parses a date value, returning undefined for invalid dates.
 * Handles both ISO strings and Unix timestamps (milliseconds).
 *
 * @param value - A date string, number (epoch ms), or null/undefined.
 * @returns A valid Date object, or undefined if parsing fails.
 */
function parseDate(value: unknown): Date | undefined {
  if (!value) return undefined
  const date = new Date(value as string | number)
  return isNaN(date.getTime()) ? undefined : date
}

/**
 * Transforms raw API response data into an HpcJob object.
 *
 * Handles both camelCase and snake_case field names from the API,
 * since the awsmap CLI returns snake_case but the proxy passes through
 * the raw response.
 *
 * @param rawJob - Raw job object from the API response.
 * @returns A typed HpcJob with proper Date fields.
 */
export function transformJobData(rawJob: Record<string, unknown>): HpcJob {
  const jobId = (rawJob.jobId ?? rawJob.job_id) as string
  const jobName = (rawJob.jobName ?? rawJob.name) as string
  const statusReason = (rawJob.statusReason ?? rawJob.status_reason) as string | undefined
  const workflowId = (rawJob.workflowId ?? rawJob.workflow_id) as string | undefined
  const arraySize = (rawJob.arraySize ?? rawJob.array_size) as number | undefined
  const arrayStatuses = (rawJob.arrayStatuses ?? rawJob.array_statuses) as Record<JobStatus, number> | undefined

  const submittedAt = rawJob.submittedAt ?? rawJob.submitted_at ?? rawJob.start_time
  const startedAt = rawJob.startedAt ?? rawJob.started_at ?? rawJob.start_time
  const stoppedAt = rawJob.stoppedAt ?? rawJob.stopped_at ?? rawJob.end_time

  // Extract dependency job IDs - the API may use "after", "depends_on", or "dependsOn"
  const dependsOnRaw = rawJob.after ?? rawJob.depends_on ?? rawJob.dependsOn
  const dependsOn = Array.isArray(dependsOnRaw) ? (dependsOnRaw as string[]) : undefined

  // Extract total cost in USD (may be "total_usd" or "totalUsd")
  const totalUsdRaw = rawJob.total_usd ?? rawJob.totalUsd
  const totalUsd = typeof totalUsdRaw === "number" ? totalUsdRaw : undefined

  // Extract explicit workflow name if provided by the backend
  const workflowName = (rawJob.workflow_name ?? rawJob.workflowName) as string | undefined

  return {
    jobId,
    jobName,
    user: rawJob.user as string,
    workflowId,
    status: rawJob.status as JobStatus,
    statusReason,
    submittedAt: parseDate(submittedAt) ?? new Date(0),
    startedAt: parseDate(startedAt),
    stoppedAt: parseDate(stoppedAt),
    arraySize,
    arrayStatuses,
    dependsOn,
    totalUsd,
    workflowName,
  }
}

/**
 * Extracts the child task index from an array job child's ID.
 *
 * Array job children are addressed as 'parentJobId:childIndex'. A plain job ID
 * has no index.
 *
 * @param jobId - The job ID to inspect.
 * @returns The child index, or undefined if the ID is not an array job child.
 */
export function getArrayChildIndex(jobId: string): number | undefined {
  const separatorIndex = jobId.lastIndexOf(":")
  if (separatorIndex === -1) return undefined

  const suffix = jobId.slice(separatorIndex + 1)
  const parsed = Number(suffix)

  // Guard against IDs that happen to contain a colon followed by non-digits;
  // Number("") is 0, so an empty suffix must be rejected explicitly.
  if (suffix === "" || !Number.isInteger(parsed) || parsed < 0) return undefined

  return parsed
}

/**
 * Sorts an array job's child tasks into ascending child index order.
 *
 * Sorts on the parsed numeric index rather than the job ID string, which would
 * otherwise order children 0, 1, 10, 100, 2. Children whose ID carries no index
 * are treated as index 0.
 *
 * @param children - The child tasks to sort. Not mutated.
 * @returns A new array ordered by child index, lowest first.
 */
export function sortChildJobsByIndex(children: HpcJob[]): HpcJob[] {
  return [...children].sort(
    (a, b) => (getArrayChildIndex(a.jobId) ?? 0) - (getArrayChildIndex(b.jobId) ?? 0)
  )
}

/**
 * Checks if a job status is considered "running" (not yet completed).
 *
 * @param status - The job status to check.
 * @returns True if the job is still in progress.
 */
export function isRunningStatus(status: JobStatus): boolean {
  return [
    JobStatus.SUBMITTED,
    JobStatus.PENDING,
    JobStatus.RUNNABLE,
    JobStatus.STARTING,
    JobStatus.RUNNING,
  ].includes(status)
}

/**
 * Formats a Date for display in the jobs table.
 * Uses Australia/Perth timezone to match the HPC team's locale.
 *
 * @param date - The date to format, or undefined.
 * @returns A formatted string like "14:30 28-05-2026", or "-" if invalid.
 */
export function formatJobTimestamp(date: Date | undefined): string {
  if (!date || isNaN(date.getTime()) || date.getTime() === 0) {
    return "-"
  }

  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Australia/Perth",
    hour12: false,
  }).format(date)
}

/**
 * Formats the elapsed run time between a job's start and end times.
 *
 * Only completed jobs get a duration: a job that has not started, or that is
 * still running (no end time), returns "-" rather than a value that would be
 * stale the moment it rendered. Units with a zero value are omitted from the
 * left, so a short job reads "45s" rather than "0d 0h 0m 45s".
 *
 * @param startedAt - When the job started running, or undefined.
 * @param stoppedAt - When the job stopped, or undefined if still running.
 * @returns A duration string like "3d 4h", "1h 23m 45s" or "45s", or "-" when
 *   the duration cannot be calculated.
 */
export function formatJobDuration(
  startedAt: Date | undefined,
  stoppedAt: Date | undefined
): string {
  if (!startedAt || !stoppedAt) return "-"

  const elapsedMs = stoppedAt.getTime() - startedAt.getTime()
  // Guard against invalid dates (NaN) and clocks that report an end before the
  // start, which would otherwise render as a negative duration.
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return "-"

  const totalSeconds = Math.floor(elapsedMs / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const units: [number, string][] = [
    [days, "d"],
    [hours, "h"],
    [minutes, "m"],
    [seconds, "s"],
  ]

  // Drop leading zero units, then keep everything after the largest non-zero
  // unit so intermediate zeros are preserved (e.g. "1h 0m 5s").
  const firstNonZero = units.findIndex(([value]) => value > 0)
  if (firstNonZero === -1) return "0s"

  return units
    .slice(firstNonZero)
    .map(([value, suffix]) => `${value}${suffix}`)
    .join(" ")
}

/**
 * Returns a Tailwind color class for a job status badge.
 *
 * @param status - The job status.
 * @returns A string of Tailwind classes for background/text color.
 */
export function getStatusVariant(status: JobStatus): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case JobStatus.RUNNING:
    case JobStatus.STARTING:
      return "default"
    case JobStatus.SUCCEEDED:
      return "secondary"
    case JobStatus.FAILED:
      return "destructive"
    case JobStatus.PENDING:
    case JobStatus.RUNNABLE:
    case JobStatus.SUBMITTED:
      return "outline"
    default:
      return "outline"
  }
}

/**
 * Display order for statuses in a per-status breakdown tooltip.
 * Completed statuses come first, followed by running statuses from most to
 * least active.
 */
const STATUS_TOOLTIP_ORDER: JobStatus[] = [
  JobStatus.SUCCEEDED,
  JobStatus.FAILED,
  JobStatus.RUNNING,
  JobStatus.STARTING,
  JobStatus.RUNNABLE,
  JobStatus.PENDING,
  JobStatus.SUBMITTED,
]

/**
 * Formats a per-status job count breakdown into a tooltip string.
 *
 * Statuses are listed in a fixed, human-friendly order (completed first, then
 * running from most to least active) and any status with a zero or missing
 * count is omitted. Shared by the array child job breakdown and the aggregated
 * workflow status breakdown so both tooltips read identically.
 *
 * @param statusCounts - A record mapping each JobStatus to a job count, or undefined.
 * @returns A comma-separated summary like "SUCCEEDED: 7, FAILED: 2, RUNNING: 3", or undefined when there is nothing to show.
 */
export function formatStatusCountsTooltip(
  statusCounts?: Partial<Record<JobStatus, number>>
): string | undefined {
  if (!statusCounts) return undefined

  const parts: string[] = []
  for (const status of STATUS_TOOLTIP_ORDER) {
    const count = statusCounts[status]
    if (count && count > 0) {
      parts.push(`${status}: ${count}`)
    }
  }

  return parts.length > 0 ? parts.join(", ") : undefined
}

/**
 * Priority order for running statuses when computing aggregate workflow status.
 * Higher index = higher priority (more "active").
 */
const RUNNING_STATUS_PRIORITY: JobStatus[] = [
  JobStatus.SUBMITTED,
  JobStatus.PENDING,
  JobStatus.RUNNABLE,
  JobStatus.STARTING,
  JobStatus.RUNNING,
]

/**
 * Computes a single aggregate status for a group of related jobs.
 *
 * Used for array jobs (grouping their child tasks). Workflows have their own
 * status from the backend; see HpcWorkflow.status.
 *
 * Logic:
 * - If any job has FAILED status, the group is FAILED
 * - If all jobs have SUCCEEDED status, the group is SUCCEEDED
 * - Otherwise, returns the most urgent running status (RUNNING > STARTING > RUNNABLE > PENDING > SUBMITTED)
 *
 * @param jobs - Array of jobs in the group, assumed to be non-empty.
 * @returns The aggregate JobStatus for the group.
 */
export function computeAggregateStatus(jobs: HpcJob[]): JobStatus {
  // Check for any failures first
  const hasFailed = jobs.some((job) => job.status === JobStatus.FAILED)
  if (hasFailed) {
    return JobStatus.FAILED
  }

  // Check if all succeeded
  const allSucceeded = jobs.every((job) => job.status === JobStatus.SUCCEEDED)
  if (allSucceeded) {
    return JobStatus.SUCCEEDED
  }

  // Find the most urgent running status
  let highestPriority = -1
  let mostUrgentStatus = JobStatus.SUBMITTED

  for (const job of jobs) {
    const priorityIndex = RUNNING_STATUS_PRIORITY.indexOf(job.status)
    if (priorityIndex > highestPriority) {
      highestPriority = priorityIndex
      mostUrgentStatus = job.status
    }
  }

  return mostUrgentStatus
}

/**
 * Counts how many jobs hold each status within a group of related jobs.
 *
 * Every JobStatus key is initialised to 0 so the returned record is complete
 * and strongly typed, letting callers index any status without an undefined
 * check. Each job is counted once by its top-level status, so the counts sum
 * to the total number of jobs in the group. Used for array job children.
 *
 * @param jobs - Array of jobs in the group.
 * @returns A record mapping each JobStatus to the number of jobs in that status.
 */
export function computeStatusCounts(jobs: HpcJob[]): Record<JobStatus, number> {
  // Initialise every status to zero so the record is complete and strongly typed.
  const counts: Record<JobStatus, number> = {
    [JobStatus.SUBMITTED]: 0,
    [JobStatus.PENDING]: 0,
    [JobStatus.RUNNABLE]: 0,
    [JobStatus.STARTING]: 0,
    [JobStatus.RUNNING]: 0,
    [JobStatus.SUCCEEDED]: 0,
    [JobStatus.FAILED]: 0,
  }

  // Count each job once by its top-level status.
  for (const job of jobs) {
    counts[job.status] += 1
  }

  return counts
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

/**
 * Parses a workflow status string from the backend.
 *
 * @param status - The status string, e.g. "RUNNING".
 * @returns The matching WorkflowStatus.
 * @throws Error if the string is not a known workflow status, so a backend
 *   change shows up as an error rather than a mislabelled badge.
 */
export function parseWorkflowStatus(status: string): WorkflowStatus {
  const known = Object.values(WorkflowStatus) as string[]
  if (!known.includes(status)) {
    throw new Error(`Unknown workflow status '${status}'`)
  }
  return status as WorkflowStatus
}

/**
 * Converts an epoch-millisecond timestamp from the backend into a Date.
 *
 * @param epochMs - Milliseconds since the epoch, or null when absent.
 * @returns The Date, or undefined when the value is null.
 */
function epochMsToDate(epochMs: number | null): Date | undefined {
  return epochMs === null ? undefined : new Date(epochMs)
}

/**
 * Transforms one entry of the /workflows response into an HpcWorkflow.
 *
 * @param entry - One workflow as returned by `awsmap batch workflows --json`.
 * @returns The typed workflow with Date fields.
 * @throws Error if the entry has an unknown status.
 */
export function transformWorkflowData(entry: WorkflowListingEntry): HpcWorkflow {
  return {
    workflowId: entry.workflow_id,
    workflowName: entry.workflow_name ?? undefined,
    user: entry.user,
    workflowKind: entry.workflow_kind,
    status: parseWorkflowStatus(entry.status),
    submittedAt: new Date(entry.submitted_at),
    startedAt: epochMsToDate(entry.started_at),
    completedAt: epochMsToDate(entry.completed_at),
    taskCount: entry.task_count,
    // Keys are job statuses; the backend only uses JobStatus values here.
    taskStateCounts: entry.task_state_counts as Partial<Record<JobStatus, number>>,
    activeTaskCount: entry.active_task_count,
    completedTaskCount: entry.completed_task_count,
    countsRefreshedAt: new Date(entry.counts_refreshed_at),
    nextRefreshAt: epochMsToDate(entry.next_refresh_at),
    // Null (no task has a rate) and absent (older awsmap) both mean no estimate.
    estimatedCostUsd: entry.estimated_cost_usd ?? undefined,
  }
}

/**
 * Checks if a workflow is still in progress (SUBMITTED, RUNNING or EXPORTING).
 *
 * Decides which tab a workflow belongs on: active workflows on "Running",
 * everything else on "Completed".
 *
 * @param status - The workflow status to check.
 * @returns True while the workflow may still change status.
 */
export function isWorkflowActive(status: WorkflowStatus): boolean {
  return [WorkflowStatus.SUBMITTED, WorkflowStatus.RUNNING, WorkflowStatus.EXPORTING].includes(
    status
  )
}

/**
 * Checks if a workflow can still be cancelled.
 *
 * Only SUBMITTED and RUNNING workflows have tasks left to cancel. An
 * EXPORTING workflow is active but all its tasks have already succeeded.
 *
 * @param status - The workflow status to check.
 * @returns True if the cancel button should be shown.
 */
export function canCancelWorkflow(status: WorkflowStatus): boolean {
  return status === WorkflowStatus.SUBMITTED || status === WorkflowStatus.RUNNING
}

/**
 * Returns the Badge variant for a workflow status.
 *
 * @param status - The workflow status.
 * @returns The Badge variant name.
 */
export function getWorkflowStatusVariant(
  status: WorkflowStatus
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case WorkflowStatus.RUNNING:
    case WorkflowStatus.EXPORTING:
      return "default"
    case WorkflowStatus.SUCCEEDED:
      return "secondary"
    case WorkflowStatus.FAILED:
      return "destructive"
    case WorkflowStatus.SUBMITTED:
    case WorkflowStatus.CANCELLED:
    case WorkflowStatus.UNKNOWN:
      return "outline"
  }
}

/**
 * Short explanation of a workflow status, used as the badge tooltip for the
 * statuses that aren't self-explanatory.
 *
 * @param status - The workflow status.
 * @returns The explanation, or undefined when the status speaks for itself.
 */
export function describeWorkflowStatus(status: WorkflowStatus): string | undefined {
  switch (status) {
    case WorkflowStatus.EXPORTING:
      return "All tasks succeeded; outputs are still being exported to S3"
    case WorkflowStatus.CANCELLED:
      return "At least one task was cancelled"
    case WorkflowStatus.UNKNOWN:
      return "The outcome of this older workflow could not be worked out"
    default:
      return undefined
  }
}

/**
 * Formats how long ago a workflow's status and counts were refreshed.
 *
 * The backend refreshes active workflows about once a minute, so minutes are
 * precise enough.
 *
 * @param refreshedAt - When the counts were last recalculated.
 * @param now - The current time.
 * @returns A string like "updated just now", "updated 3 min ago" or
 *   "updated 2 h ago".
 */
export function formatRefreshedAgo(refreshedAt: Date, now: Date): string {
  // Clamp at zero so a slightly fast server clock doesn't show a negative age.
  const elapsedMs = Math.max(0, now.getTime() - refreshedAt.getTime())
  const minutes = Math.floor(elapsedMs / 60_000)

  if (minutes < 1) return "updated just now"
  if (minutes < 60) return `updated ${minutes} min ago`
  return `updated ${Math.floor(minutes / 60)} h ago`
}

/**
 * Formats a USD cost value for display.
 *
 * @param cost - The cost in USD, or undefined if not available.
 * @returns A formatted string like "$1.23", or "-" if cost is undefined.
 */
export function formatCostUsd(cost: number | undefined): string {
  if (cost === undefined) {
    return "-"
  }
  return `$${cost.toFixed(2)}`
}

// ---------------------------------------------------------------------------
// Job date-range look-back
// ---------------------------------------------------------------------------

/**
 * Selectable look-back windows (in days) for the HPC jobs list.
 *
 * Rendered as a segmented toggle on the jobs pages and used to validate the
 * `range` URL parameter. Declared `as const` so the values become a literal
 * union (see {@link JobDateRange}) rather than widening to `number`.
 */
export const JOB_DATE_RANGE_OPTIONS = [7, 30] as const

/** A look-back window (in days) supported by the jobs pages: 7 or 30. */
export type JobDateRange = (typeof JOB_DATE_RANGE_OPTIONS)[number]

/** Default look-back window applied when the user has not chosen one. */
export const DEFAULT_JOB_DATE_RANGE: JobDateRange = 7

/**
 * Parses a raw URL query value into a supported {@link JobDateRange}.
 *
 * Any value that is not one of {@link JOB_DATE_RANGE_OPTIONS} (including null,
 * empty, or non-numeric input) resolves to {@link DEFAULT_JOB_DATE_RANGE}. This
 * validates untrusted URL input rather than masking an application error, so a
 * hand-edited or stale `range` param cannot break the page.
 *
 * @param raw - The raw string from the URL search params, or null when absent.
 * @returns A valid JobDateRange (defaults to 7 days).
 */
export function parseJobDateRange(raw: string | null): JobDateRange {
  const parsed = Number(raw)
  return JOB_DATE_RANGE_OPTIONS.includes(parsed as JobDateRange)
    ? (parsed as JobDateRange)
    : DEFAULT_JOB_DATE_RANGE
}

/**
 * Computes the ISO date (YYYY-MM-DD) that falls `days` days before today.
 *
 * Used to build the `start` query parameter for the list-jobs endpoint so the
 * backend only returns jobs submitted on or after that date. The date is
 * derived from the local calendar day (not UTC) so the boundary matches the
 * day the user sees; using toISOString() would shift the result by a day for
 * users in negative/positive UTC offsets.
 *
 * @param days - How many days back the start boundary should be (e.g. 7 or 30).
 * @returns The start date formatted as a zero-padded YYYY-MM-DD string.
 */
export function isoDateDaysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)

  // Build the string from local date components to avoid the UTC conversion
  // that toISOString() applies, which can roll the date back/forward a day.
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}
